/* eslint-disable no-nested-ternary */
/* eslint-disable react/no-unstable-nested-components */
import React, { useMemo } from 'react';

import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useSelectedFeedbackViewFromParam } from 'screens/Levelup/hooks/useSelectedFeedbackViewFromParam';
import { selectLoggedInEmployeeId } from 'selectors/loggedInEmployee';

import { RequestFeedback, ShareFeedback } from 'assets/assetsV1/iconComponents';
import NoFeedback from 'assets/assetsV1/illustrations/newIllustrations/noFeedback.svg';

import { FEEDBACK_FORM_TYPE, MODULE_KEYS_USER_PREFERENCE_PERSISTENCE } from 'constants/ProjectConstants';
import { getPersistenceKey, pushFeedbackParams } from 'constants/helper';
import { isMobileView } from 'constants/isMobileView';
import { EVENT_SOURCES } from 'constants/trackingEvents';

import { AgGridHeader } from 'uiToolkitV1/AgGridHeader';
import { AgGridWrapper } from 'uiToolkitV1/AgGridWrapper';
import { Button } from 'uiToolkitV1/Button';
import { EmptyScreen } from 'uiToolkitV1/EmptyScreen';
import { Paper } from 'uiToolkitV1/Paper';
import { SkeletonLoader } from 'uiToolkitV1/SkeletonLoader';

import { openFeedbackSidePanel } from '../helpers';
import AddFeedbackButton from './addFeedbackButton/AddFeedbackButton';

const formData = {
  SHARED: 'SHOW_PROVIDED',
  RECEIVED: 'SHOW_RECEIVED',
  REQUESTED: 'SHOW_REQUESTED',
};

export const RequestFeedbackIcon = ({ ...props }) => <RequestFeedback {...props} fill="transparent" color="white" />;
const ShareFeedbackIcon = ({ ...props }) => <ShareFeedback {...props} fill="transparent" />;

const CFTablesV1 = ({ gridRef, cfTableData, columnDefs, isCfTableLoading, selectedTab, responsiveColumnDef }) => {
  const history = useHistory();

  const { selectedFeedbackView } = useSelectedFeedbackViewFromParam();

  const defaultColDef = useMemo(
    () => ({
      flex: 1,
      sortable: true,
      menuTabs: ['filterMenuTab'],
      headerComponent: AgGridHeader,
    }),
    []
  );

  const loggedInEmployeeId = useSelector(selectLoggedInEmployeeId);

  const isPendingType = selectedTab === 'PENDING';
  const isReceivedType = selectedTab === 'RECEIVED';
  const isSharedType = selectedTab === 'SHARED';
  const isRequestedType = selectedTab === 'REQUESTED';

  const handleShareNewFeedbackClick = (isSharePublicPraise = false) => {
    pushFeedbackParams({
      history,
      formType: FEEDBACK_FORM_TYPE.PROVIDED,
      newForm: true,
      empId: null,
      source: EVENT_SOURCES.FEEDBACK_PAGE.MY_FEEDBACK,
      isSharePublicPraise,
      selectedFeedbackView,
    });
  };

  const handleRequestNewFeedbackClick = () => {
    pushFeedbackParams({
      history,
      formType: FEEDBACK_FORM_TYPE.REQUESTED,
      newForm: true,
      empId: loggedInEmployeeId ?? null,
      source: EVENT_SOURCES.FEEDBACK_PAGE.MY_FEEDBACK,
      selectedFeedbackView,
    });
  };

  const rowClickHandler = ({ data, column }) => {
    const { uuid: feedbackId } = data.requestDetails ?? data.comment ?? {};
    const formType = formData[selectedTab];

    if (column.colId === 'kebab-menu') {
      //  To prevent event bubbling.
    } else {
      // eslint-disable-next-line no-unused-expressions
      isPendingType
        ? openFeedbackSidePanel({ feedbackId, history, selectedFeedbackView })
        : (isReceivedType || isSharedType || isRequestedType) &&
          pushFeedbackParams({
            history,
            formType,
            typeMapped: true,
            feedbackId,
            selectedFeedbackView,
          });
    }
  };

  /* Grid State persistence Key */
  const gridStatePersistenceKey = useMemo(() => {
    const moduleName = MODULE_KEYS_USER_PREFERENCE_PERSISTENCE.FEEDBACK;
    const viewId = 'My_Feedback';
    const tabId = 'Explore';
    const uniqueId = 'Pending_Feedback_Grid';

    return getPersistenceKey({
      moduleName,
      viewId,
      tabId,
      uniqueId,
    });
  }, []);

  if (isCfTableLoading) {
    return <SkeletonLoader />;
  }

  if (!cfTableData?.values?.length) {
    const heading = isPendingType
      ? 'No pending feedback'
      : isSharedType
        ? 'No feedback shared yet'
        : isRequestedType
          ? 'No feedback requests active'
          : "There's no Feedback yet";
    const description = isPendingType
      ? 'Why not share some feedback? Let your thoughts be heard!'
      : isSharedType
        ? 'Share feedback for colleagues now and let them know what you think'
        : isRequestedType
          ? 'Go ahead and request feedback from someone now! Unleash the power of input!'
          : 'Why not request or share some feedback?';

    return (
      <div style={{ marginTop: '60px' }}>
        <EmptyScreen
          heading={heading}
          description={description}
          illustrationSrc={NoFeedback}
          CTAComp={() => {
            if (isReceivedType) {
              return (
                <AddFeedbackButton
                  onClickShareFeedback={(isSharePublicPraise) => handleShareNewFeedbackClick(isSharePublicPraise)}
                  onClickRequestFeedback={handleRequestNewFeedbackClick}
                />
              );
            }
            if (isRequestedType) {
              <Button StartIcon={RequestFeedbackIcon} onClick={handleRequestNewFeedbackClick}>
                Request Feedback
              </Button>;
            }
            return (
              <Button StartIcon={ShareFeedbackIcon} onClick={handleShareNewFeedbackClick}>
                Share Feedback
              </Button>
            );
          }}
        />
      </div>
    );
  }

  return (
    <Paper
      pt={isMobileView() ? '10px' : '22px'}
      className={`ag-theme-alpine goals-ag-grid ${isMobileView() ? 'mobile-style' : ''}`}
    >
      <AgGridWrapper
        key={gridStatePersistenceKey} // we want to re-initialize on key change
        gridStatePersistenceKey={gridStatePersistenceKey}
        pagination
        suppressPaginationPanel={
          cfTableData?.values?.length < 6 ||
          cfTableData?.values?.length === undefined ||
          cfTableData?.values?.length === 0
        }
        domLayout="autoHeight"
        animateRows={false}
        ref={gridRef}
        rowData={cfTableData?.values}
        columnDefs={isMobileView() ? responsiveColumnDef : columnDefs}
        defaultColDef={defaultColDef}
        style={{ width: '100%', height: '100%' }}
        rowHeight={74}
        rowStyle={{ cursor: 'pointer' }}
        paginationPageSize={isMobileView() ? 5 : 10}
        sortable
        onCellClicked={rowClickHandler}
        cacheQuickFilter
      />
    </Paper>
  );
};

export default CFTablesV1;
